/**
 * A form's field kinds as a map: per kind its spec, its form value and the
 * issues its rules report. The schema form engine is generic over one such
 * map (`M`); a feature brings the map and a kit for it (components, rules,
 * copy). Lookups by `spec.kind` stay correlated: a kind's component, rules
 * and message only ever see that kind's spec, value and issue.
 */

/** What a field's rules report, without words; `at` points inside a composite value. */
export type IssueBase = { code: string; at?: string };

/** What the engine itself reads from any field spec. */
export type FieldSpecBase<K extends string> = {
  kind: K;
  /** Stable within a unit: 'core:emails', 'custom:42'. */
  key: string;
  label: string;
  required: boolean;
};

export type KindDef<K extends string> = {
  spec: FieldSpecBase<K>;
  value: unknown;
  issue: IssueBase;
};

export type KindOf<M> = keyof M & string;
export type KindMap<M> = { [K in KindOf<M>]: KindDef<K> };

export type SpecOf<M extends KindMap<M>, K extends KindOf<M>> = M[K]['spec'];
export type ValueOf<M extends KindMap<M>, K extends KindOf<M>> = M[K]['value'];
export type IssueOf<M extends KindMap<M>, K extends KindOf<M>> = M[K]['issue'];

/** Any kind's spec, each still tied to its own `kind`. */
export type AnySpec<M extends KindMap<M>> = {
  [K in KindOf<M>]: SpecOf<M, K>;
}[KindOf<M>];
export type AnyValue<M extends KindMap<M>> = {
  [K in KindOf<M>]: ValueOf<M, K>;
}[KindOf<M>];

export type ValidationResult<I> =
  | { type: 'valid' }
  | { type: 'invalid'; issue: I };

/** Values of one unit, keyed by field key; `readField` recovers a field's own type. */
export type UnitValues<M extends KindMap<M>> = {
  [fieldKey: string]: AnyValue<M>;
};

/**
 * A field's value in its kind's type. The one cast between a form's string
 * keys and the kinds: the value under `spec.key` was written for this very
 * spec (decoded or emptied by its kind).
 */
export function readField<M extends KindMap<M>, K extends KindOf<M>>(
  values: UnitValues<M>,
  spec: SpecOf<M, K>
): ValueOf<M, K> {
  if (!(spec.key in values)) {
    throw new Error(`No value for field "${spec.key}"`);
  }
  return values[spec.key] as ValueOf<M, K>;
}

/**
 * New values for fields of some kinds, each handler seeing its own kind's
 * spec and value; `undefined` leaves a field as it is.
 */
export type FieldUpdates<M extends KindMap<M>> = {
  [K in KindOf<M>]?: (
    spec: SpecOf<M, K>,
    value: ValueOf<M, K>
  ) => ValueOf<M, K> | undefined;
};

function updatedValue<M extends KindMap<M>, K extends KindOf<M>>(
  updates: FieldUpdates<M>,
  spec: SpecOf<M, K>,
  values: UnitValues<M>
): ValueOf<M, K> | undefined {
  const update = updates[spec.kind];
  return update === undefined
    ? undefined
    : update(spec, readField<M, K>(values, spec));
}

/** A unit's values after `updates`; `null` when they touch none of its fields. */
export function applyFieldUpdates<M extends KindMap<M>>(
  fields: ReadonlyArray<AnySpec<M>>,
  values: UnitValues<M>,
  updates: FieldUpdates<M>
): UnitValues<M> | null {
  let next: UnitValues<M> | null = null;
  for (const field of fields) {
    const value = updatedValue(updates, field, values);
    if (value === undefined) continue;
    next = { ...(next ?? values), [field.key]: value };
  }
  return next;
}
